import styled from '@emotion/styled';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogContent, DialogTitle, IconButton, useTheme } from '@mui/material';
import { useEffect } from 'react';

import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { pricingPageUrl } from '@/environment';
import PartyBackground from '@/images/PartyBackground.png';
import { useAnalytics } from '@/services/analytics';
import theme from '@/theme';
import Lightning from '../../images/FlashLightning.svg';
import Button from '../Buttons/Button';
import { MaskedIcon } from '../MaskedIcon/MaskedIcon';

interface Props {
    open: boolean;
    onClose: () => void;
    from: string;
    title: string | React.ReactNode;
    imageFile: string;
    ctaAction?: string | (() => void);
    ctaText?: string;
    features?: string[];
    subheading?: string;
}

const TextP = styled.p`
    font-weight: 700;
    font-size: 2rem;
    margin: 10px;
    color: ${props => props.color};
`;

const Title = styled.div`
    font-size: 3.5rem;
    margin-bottom: 0.5rem;
    margin-top: 3.5rem;
    line-height: 1.2;
    font-weight: 700;
    text-align: center;
`;

const FeatureContainer = styled.div`
    display: flex;
    flex-direction: row;
    padding: 0.6rem 20px;
    font-size: 1.5rem;
`;

const StyledDialogTitle = styled(DialogTitle)`
    font-size: 1.6rem;
    justify-content: center;
`;

const FeaturesGrid = styled.div`
    display: grid;
    grid-template-columns: auto auto auto;
`;

const StyledCheckCircleOutlineIcon = styled(CheckCircleOutlineIcon)`
    color: ${({ theme }) => theme.colors.primary.main};
    margin-right: 1rem;
`;

const StyledIconButton = styled(IconButton)`
    position: absolute;
    right: 1rem;
    color: ${theme.colors.primary};
    top: 0.5rem;
`;

const StyledButton = styled(Button)`
    padding: 0.5rem 5rem;
    font-size: 1.6rem;
    border-radius: 8px;
    line-height: 1;
    font-weight: 700;
    padding: 10px 60px;
`;

const StyledDialogActions = styled(DialogActions)`
    margin-top: 1.75rem;
    margin-bottom: 2.5rem;
`;

const Image = styled.img`
    width: 100%;
    padding: 2rem 8rem;
`;

// TODO: replace/remove the "more credits"
const FEATURES_DATA = [
    'More minutes',
    'More credits',
    'Additional AI generated avatars',
    'Access to additional voices',
    'Access to plugins',
    'Additional features',
];

export default function ({ open, onClose, from, title, imageFile, ctaText, ctaAction = pricingPageUrl, features, subheading }: Props) {
    const mixpanel = useAnalytics();
    const isCtaUrl = typeof ctaAction === 'string';
    const theme = useTheme();

    useEffect(() => {
        if (open) {
            mixpanel?.track('missing-features', { event: 'view', from, ctaUrl: isCtaUrl ? ctaAction : 'signup' });
        }
    }, [open]);

    const onClick = () => {
        mixpanel?.track('missing-features', { event: 'click', from, ctaUrl: isCtaUrl ? ctaAction : 'signup' });
        if (isCtaUrl) {
            const url = ctaAction === pricingPageUrl ? `${pricingPageUrl}/?utm_source=${from}` : ctaAction;
            window.open(url, '_blank');
        } else {
            ctaAction();
        }
        onClose();
    };

    const renderFeatures = () => (
        <FeaturesGrid>
            {(features ?? FEATURES_DATA).map((feature, index) => (
                <FeatureContainer key={index}>
                    <StyledCheckCircleOutlineIcon fontSize="large" />
                    {feature}
                </FeatureContainer>
            ))}
        </FeaturesGrid>
    );

    return (
        <BootstrapDialog
            onClose={onClose}
            open={open}
            width="80rem"
            contentstyle={{ flex: 1, overflow: 'auto', alignItems: 'center' }}
            PaperProps={{ style: { borderRadius: '12px', backgroundImage: `url(${PartyBackground})`, backgroundSize: 'cover' } }}>
            <StyledDialogTitle id="delete-image-modal">
                <Title>{title}</Title>
                <StyledIconButton aria-label="close" onClick={onClose}>
                    <CloseIcon />
                </StyledIconButton>
            </StyledDialogTitle>
            <DialogContent>
                <Image src={imageFile} />
                {<TextP> {subheading ? subheading : 'You will also get'} </TextP>}
                {renderFeatures()}
            </DialogContent>
            <StyledDialogActions>
                <StyledButton
                    onClick={onClick}
                    aria-label="delete"
                    startIcon={<MaskedIcon src={Lightning} size="24px" color={theme.colors.black.main} />}>
                    {ctaText ?? 'Upgrade me'}
                </StyledButton>
            </StyledDialogActions>
        </BootstrapDialog>
    );
}
